import React from 'react';
import { formatDateOnly, formatMonthOnly } from '../../utils/dates';
import { personName } from '../../utils/personName';

// U+2212 MINUS SIGN, not a hyphen. A negative balance is the one number on this
// screen a member reads at a glance in a dim room, and the hyphen is too light
// at 44px to register as "you owe".
const MINUS = '−';

export function money(amount) {
  const value = Number(amount) || 0;
  return `${value < 0 ? MINUS : ''}$${Math.abs(value).toFixed(2)}`;
}

export function signedMoney(amount) {
  const value = Number(amount) || 0;
  return `${value > 0 ? '+' : ''}${money(value)}`;
}

/*
 * Both of these used to assume a real name was on file, and returned '?' and an
 * empty string when none was. That was safe while every account came from an
 * application form. It stopped being safe when an account could be created at a
 * machine from an email address and a password, which leaves first and last name
 * genuinely null: the identity header rendered a blank heading and the
 * membership card a blank name.
 *
 * So they fall through to what the club does know. stern_username comes before
 * username deliberately: it is the name the person chose and the one their
 * scores appear under, where username is derived from their email address and
 * is not something they picked or would recognise.
 */
function knownNamesFor(user) {
  const full = [user.first_name, user.last_name].filter(Boolean).join(' ');
  return [user.preferred_name, full, user.stern_username, user.username].filter(Boolean);
}

export function displayNameFor(user) {
  if (!user) return '';
  return personName(user) || knownNamesFor(user)[0] || '';
}

// Both format in UTC. These render DATE columns - a start date, a member-since -
// and rendering those in the browser's zone moves them a day back for everyone
// west of UTC. formatMonth was off by a whole month for a 1 January start.
export function formatDay(value) {
  return formatDateOnly(value);
}

export function formatMonth(value) {
  return formatMonthOnly(value);
}

export function formatWhen(value) {
  if (!value) return '';
  const date = new Date(value);
  const today = new Date();
  const sameDay = date.toDateString() === today.toDateString();
  const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  if (sameDay) {
    return `Today, ${time}`;
  }
  return `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${time}`;
}

// "In 2 days" / "Tomorrow" / a date. Used on the Assigned tab, where the only
// thing that matters about a date is how soon it is.
export function relativeDay(value) {
  if (!value) return null;
  const date = new Date(value);
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOfDay(date) - startOfDay(new Date())) / 86400000);

  if (days < 0) return `${Math.abs(days)} ${Math.abs(days) === 1 ? 'day' : 'days'} ago`;
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days <= 6) return `In ${days} days`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function ordinal(n) {
  const value = Number(n);
  if (!value) return null;
  const rem100 = value % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${value}th`;
  switch (value % 10) {
    case 1: return `${value}st`;
    case 2: return `${value}nd`;
    case 3: return `${value}rd`;
    default: return `${value}th`;
  }
}

// (512) 555-0148. Stored digits are what the member typed, which is usually a bare
// run of ten, and a bare run of ten is genuinely hard to read back. Formats only
// what it recognises: anything that is not a 10-digit or 1+10-digit US number is
// shown exactly as entered, since guessing at an international format would be
// worse than leaving it alone.
export function formatPhone(value) {
  if (!value) return value;

  const digits = String(value).replace(/\D/g, '');
  const national = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;

  if (national.length !== 10) {
    return value;
  }

  return `(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`;
}

// A label/value row with a trailing control. Used by Your details, Gaming
// accounts, and Membership, which is why the trailing control is a slot: the
// three sections differ only in what sits on the right.
export function DetailRow({ label, value, emptyText = 'Not set', action }) {
  return (
    <div className="mm-row">
      <span className="mm-row__main">
        <span className="mm-row__label">{label}</span>
        <span className={`mm-row__value ${value ? '' : 'mm-row__value--empty'}`}>
          {value || emptyText}
        </span>
      </span>
      {action}
    </div>
  );
}
